import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import './Settings.css';

interface SystemSettings {
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  taxNumber: string;
  bankAccount: string;
  defaultCurrency: string;
  timezone: string;
  dateFormat: string;
  language: string;
}

interface BookingSettings {
  minParticipants: number;
  maxParticipants: number;
  depositPercentage: number;
  cancellationDeadline: number;
  autoConfirmBookings: boolean;
  requireEmailVerification: boolean;
  allowGuestBookings: boolean;
  bookingLeadTime: number;
  maxAdvanceBookingDays: number;
}

interface EmailSettings {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  smtpSecure: boolean;
  fromEmail: string;
  fromName: string;
  emailSignature: string;
  enableEmailNotifications: boolean;
}

interface PaymentSettings {
  stripeEnabled: boolean;
  stripePublicKey: string;
  stripeSecretKey: string;
  paypalEnabled: boolean;
  paypalClientId: string;
  paypalSecretKey: string;
  bankTransferEnabled: boolean;
  cashPaymentEnabled: boolean;
  invoicePaymentEnabled: boolean;
}
interface PricingSettings {
  basePricePerPerson: number;
  premiumPricePerPerson: number;
  allInPricePerPerson: number;
  childDiscount: number;
  groupDiscount: number;
  seasonalPricing: boolean;
  dynamicPricing: boolean;
  vatRate: number;
}

interface NotificationSettings {
  emailReminders: boolean;
  smsReminders: boolean;
  whatsappEnabled: boolean;
  reminderDaysBefore: number;
  sendBookingConfirmation: boolean;
  sendPaymentConfirmation: boolean;
  sendCancellationNotice: boolean;
  adminNotifications: boolean;
}

interface UserManagement {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Staff' | 'Support';
  permissions: string[];
  lastLogin: string;
  status: 'active' | 'inactive';
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('system');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // System Settings State
  const [systemSettings, setSystemSettings] = useState<SystemSettings>({
    companyName: 'Frankenfloss GmbH',
    companyEmail: 'info@frankenfloss.de',
    companyPhone: '+49 123 456789',
    companyAddress: 'Flussstraße 1, 90402 Nürnberg',
    taxNumber: 'DE123456789',
    bankAccount: 'DE89 3704 0044 0532 0130 00',
    defaultCurrency: 'EUR',
    timezone: 'Europe/Berlin',
    dateFormat: 'DD.MM.YYYY',
    language: 'de'
  });
  // Booking Settings State
  const [bookingSettings, setBookingSettings] = useState<BookingSettings>({
    minParticipants: 30,
    maxParticipants: 140,
    depositPercentage: 20,
    cancellationDeadline: 14,
    autoConfirmBookings: false,
    requireEmailVerification: true,
    allowGuestBookings: false,
    bookingLeadTime: 7,
    maxAdvanceBookingDays: 365
  });

  // Email Settings State
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUser: 'noreply@frankenfloss.de',
    smtpPassword: '********',
    smtpSecure: true,
    fromEmail: 'noreply@frankenfloss.de',
    fromName: 'Frankenfloss',
    emailSignature: 'Mit freundlichen Grüßen\nIhr Frankenfloss Team',
    enableEmailNotifications: true
  });

  // Payment Settings State
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings>({
    stripeEnabled: true,
    stripePublicKey: 'pk_test_51234567890',
    stripeSecretKey: 'sk_test_********',
    paypalEnabled: true,
    paypalClientId: 'AYZ123456789',
    paypalSecretKey: '********',
    bankTransferEnabled: true,
    cashPaymentEnabled: true,
    invoicePaymentEnabled: true
  });
  // Pricing Settings State
  const [pricingSettings, setPricingSettings] = useState<PricingSettings>({
    basePricePerPerson: 39.90,
    premiumPricePerPerson: 63.50,
    allInPricePerPerson: 89.90,
    childDiscount: 50,
    groupDiscount: 10,
    seasonalPricing: true,
    dynamicPricing: false,
    vatRate: 19
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailReminders: true,
    smsReminders: false,
    whatsappEnabled: true,
    reminderDaysBefore: 3,
    sendBookingConfirmation: true,
    sendPaymentConfirmation: true,
    sendCancellationNotice: true,
    adminNotifications: true
  });

  // Users Mock Data
  const [users, setUsers] = useState<UserManagement[]>([
    {
      id: '1',
      name: 'Admin User',
      email: 'admin@frankenfloss.de',
      role: 'Admin',
      permissions: ['all'],
      lastLogin: '2025-09-14T10:30:00',
      status: 'active'
    },
    {
      id: '2',
      name: 'Sarah Meyer',
      email: 'sarah@frankenfloss.de',
      role: 'Staff',
      permissions: ['bookings', 'customers', 'calendar'],
      lastLogin: '2025-09-14T09:15:00',
      status: 'active'
    },
    {
      id: '3',
      name: 'Tom Schmidt',
      email: 'tom@frankenfloss.de',
      role: 'Support',
      permissions: ['customers', 'support'],
      lastLogin: '2025-09-13T16:45:00',
      status: 'active'
    }
  ]);
  const handleSaveSettings = () => {
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }, 1000);
  };

  const handleResetSettings = () => {
    if (window.confirm('Möchten Sie wirklich alle Einstellungen auf Standardwerte zurücksetzen?')) {
      // Reset to defaults
      window.location.reload();
    }
  };

  const handleTestEmail = () => {
    alert('Test-E-Mail wurde versendet an: ' + emailSettings.fromEmail);
  };

  const handleBackup = () => {
    const backupData = {
      systemSettings,
      bookingSettings,
      emailSettings,
      paymentSettings,
      pricingSettings,
      notificationSettings,
      timestamp: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `frankenfloss-backup-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const tabs = [
    { id: 'system', label: 'System', icon: '⚙️' },
    { id: 'booking', label: 'Buchungen', icon: '📅' },
    { id: 'email', label: 'E-Mail', icon: '📧' },
    { id: 'payment', label: 'Zahlungen', icon: '💳' },
    { id: 'pricing', label: 'Preise', icon: '💰' },
    { id: 'notifications', label: 'Benachrichtigungen', icon: '🔔' },
    { id: 'users', label: 'Benutzer', icon: '👥' },
    { id: 'backup', label: 'Backup', icon: '💾' }
  ];
  return (
    <div className="settings-container">
      {/* Header */}
      <div className="settings-header">
        <h2>Einstellungen</h2>
        <p>Verwalten Sie alle Systemeinstellungen und Konfigurationen</p>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div className="success-message">
          ✅ Einstellungen erfolgreich gespeichert!
        </div>
      )}

      {/* Tab Navigation */}
      <div className="settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="settings-content">        {/* System Settings */}
        {activeTab === 'system' && (
          <div className="settings-section">
            <h3>Systemeinstellungen</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Firmenname</label>
                <input
                  type="text"
                  value={systemSettings.companyName}
                  onChange={(e) => setSystemSettings({...systemSettings, companyName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>E-Mail</label>
                <input
                  type="email"
                  value={systemSettings.companyEmail}
                  onChange={(e) => setSystemSettings({...systemSettings, companyEmail: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input
                  type="tel"
                  value={systemSettings.companyPhone}
                  onChange={(e) => setSystemSettings({...systemSettings, companyPhone: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input
                  type="text"
                  value={systemSettings.companyAddress}
                  onChange={(e) => setSystemSettings({...systemSettings, companyAddress: e.target.value})}
                />
              </div>              <div className="form-group">
                <label>Steuernummer</label>
                <input
                  type="text"
                  value={systemSettings.taxNumber}
                  onChange={(e) => setSystemSettings({...systemSettings, taxNumber: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Bankverbindung</label>
                <input
                  type="text"
                  value={systemSettings.bankAccount}
                  onChange={(e) => setSystemSettings({...systemSettings, bankAccount: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Währung</label>
                <select
                  value={systemSettings.defaultCurrency}
                  onChange={(e) => setSystemSettings({...systemSettings, defaultCurrency: e.target.value})}
                >
                  <option value="EUR">EUR - Euro</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
              </div>
              <div className="form-group">
                <label>Zeitzone</label>
                <select
                  value={systemSettings.timezone}
                  onChange={(e) => setSystemSettings({...systemSettings, timezone: e.target.value})}
                >
                  <option value="Europe/Berlin">Europe/Berlin</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="America/New_York">America/New_York</option>
                </select>
              </div>
            </div>
          </div>
        )}
        {/* Booking Settings */}
        {activeTab === 'booking' && (
          <div className="settings-section">
            <h3>Buchungseinstellungen</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Min. Teilnehmer</label>
                <input type="number" value={bookingSettings.minParticipants} 
                  onChange={(e) => setBookingSettings({...bookingSettings, minParticipants: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>Max. Teilnehmer</label>
                <input type="number" value={bookingSettings.maxParticipants}
                  onChange={(e) => setBookingSettings({...bookingSettings, maxParticipants: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>Anzahlung (%)</label>
                <input type="number" value={bookingSettings.depositPercentage}
                  onChange={(e) => setBookingSettings({...bookingSettings, depositPercentage: parseInt(e.target.value)})} />
              </div>
              <div className="form-group">
                <label>Stornierungsfrist (Tage)</label>
                <input type="number" value={bookingSettings.cancellationDeadline}
                  onChange={(e) => setBookingSettings({...bookingSettings, cancellationDeadline: parseInt(e.target.value)})} />
              </div>
              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input type="checkbox" checked={bookingSettings.autoConfirmBookings}
                    onChange={(e) => setBookingSettings({...bookingSettings, autoConfirmBookings: e.target.checked})} />
                  Buchungen automatisch bestätigen
                </label>
              </div>
            </div>
          </div>
        )}
        {/* Payment Settings */}
        {activeTab === 'payment' && (
          <div className="settings-section">
            <h3>Zahlungseinstellungen</h3>
            <div className="payment-providers">
              <div className="provider-card">
                <h4>💳 Stripe</h4>
                <label className="toggle-switch">
                  <input type="checkbox" checked={paymentSettings.stripeEnabled}
                    onChange={(e) => setPaymentSettings({...paymentSettings, stripeEnabled: e.target.checked})} />
                  <span className="toggle-slider"></span>
                </label>
                {paymentSettings.stripeEnabled && (
                  <div className="provider-config">
                    <input type="text" placeholder="Public Key" value={paymentSettings.stripePublicKey}
                      onChange={(e) => setPaymentSettings({...paymentSettings, stripePublicKey: e.target.value})} />
                    <input type="password" placeholder="Secret Key" value={paymentSettings.stripeSecretKey}
                      onChange={(e) => setPaymentSettings({...paymentSettings, stripeSecretKey: e.target.value})} />
                  </div>
                )}
              </div>
              <div className="provider-card">
                <h4>🅿️ PayPal</h4>
                <label className="toggle-switch">
                  <input type="checkbox" checked={paymentSettings.paypalEnabled}
                    onChange={(e) => setPaymentSettings({...paymentSettings, paypalEnabled: e.target.checked})} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}
        {/* Users Management */}
        {activeTab === 'users' && (
          <div className="settings-section">
            <h3>Benutzerverwaltung</h3>
            <button className="btn btn-primary mb-20">➕ Neuer Benutzer</button>
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th>Rolle</th>
                    <th>Status</th>
                    <th>Letzter Login</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td><span className="role-badge">{user.role}</span></td>
                      <td><span className={`status-badge ${user.status}`}>{user.status}</span></td>
                      <td>{new Date(user.lastLogin).toLocaleString('de-DE')}</td>
                      <td>
                        <button className="btn-icon">✏️</button>
                        <button className="btn-icon">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Backup & Recovery */}
        {activeTab === 'backup' && (
          <div className="settings-section">
            <h3>Backup & Wiederherstellung</h3>
            <div className="backup-options">
              <div className="backup-card">
                <h4>📥 Daten exportieren</h4>
                <p>Erstellen Sie ein vollständiges Backup aller Systemdaten</p>
                <button onClick={handleBackup} className="btn btn-primary">
                  Backup erstellen
                </button>
              </div>
              <div className="backup-card">
                <h4>📤 Daten importieren</h4>
                <p>Stellen Sie Daten aus einem Backup wieder her</p>
                <input type="file" accept=".json" className="file-input" />
                <button className="btn btn-secondary">Import starten</button>
              </div>
              <div className="backup-card">
                <h4>🔄 Automatische Backups</h4>
                <p>Täglich um 03:00 Uhr</p>
                <label className="toggle-switch">
                  <input type="checkbox" checked={true} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="settings-actions">
        <button onClick={handleResetSettings} className="btn btn-secondary">
          🔄 Zurücksetzen
        </button>
        {activeTab === 'email' && (
          <button onClick={handleTestEmail} className="btn btn-secondary">
            📧 Test-E-Mail senden
          </button>
        )}
        <button onClick={handleSaveSettings} className="btn btn-primary" disabled={isSaving}>
          {isSaving ? '⏳ Speichern...' : '💾 Einstellungen speichern'}
        </button>
      </div>
    </div>
  );
};

export default Settings;